import { Checkbox, Flex, Input, SimpleGrid, useMantineTheme } from '@mantine/core';
import { IconTrashX } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { DeviceFaultFormData } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultForm';
import { DISCONTINUED_FAULT_SEVERITY_IDS, FaultSeveritySelect } from 'components/selects/FaultSeveritySelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import RevisionPredefinedDataPicker from 'components/inputs/revision-predefined-data/RevisionPredefinedDataPicker';
import { useConfirm } from 'components/modals/message/MessageProvider';
import { IFormInput } from 'pages/revisions-module/template-editor/editors/form/types';
import DeviceFaultAttachments from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultAttachments';
import { IFormInputSpecFileList } from 'pages/revisions-module/template-editor/editors/form/types';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { AuthListFaultSeveritiesResponse } from 'api/actions/auth-list-fault-severities/auth-list-fault-severities-response';
import ActionIcon from 'components/ActionIcon';
import { useRevisionPredefinedDataCatalog } from 'components/inputs/revision-predefined-data/useRevisionPredefinedDataCatalog';
import { createOneTimeRevisionPredefinedDataOption } from 'components/inputs/revision-predefined-data/helpers';

interface DeviceFaultFormRowProps {
  deviceTypeId: number;
  deviceTypeName: string;
  revisionId: number;
  form: UseFormReturnType<DeviceFaultFormData, (values: DeviceFaultFormData) => DeviceFaultFormData>;
  index: number;
  faultInput: IFormInput;
  severityInput: IFormInput;
  moreInfoInput: IFormInput;
  observationInput: IFormInput;
  attachmentsInput: IFormInput;
  onDelete?: (index: number) => void;
}

/**
 * Renders a row of a device fault form.
 */
export function DeviceFaultFormRow({
  deviceTypeId,
  deviceTypeName,
  revisionId,
  form,
  index,
  faultInput,
  severityInput,
  moreInfoInput,
  observationInput,
  attachmentsInput,
  onDelete = noop,
}: DeviceFaultFormRowProps): JSX.Element {
  const theme = useMantineTheme();
  const { getAction } = useApi();
  const { confirm } = useConfirm();
  const [faultSeverities, setFaultSeverities] = useState<AuthListFaultSeveritiesResponse>([]);
  const faultCatalog = useRevisionPredefinedDataCatalog({
    deviceTypeId,
    predefinedDataType: 'Nedostatky',
    revisionId,
  });

  const { value: severityId } = form.getInputProps(`faults.${index}.severity`);
  const { value: faultValue } = form.getInputProps(`faults.${index}.fault`);
  const { value: faultDescription } = form.getInputProps(`faults.${index}.description`);

  const faultOptions = useMemo(() => {
    if (!faultValue || faultCatalog.options.some((option) => option.value === faultValue)) {
      return faultCatalog.options;
    }

    return [
      createOneTimeRevisionPredefinedDataOption({ name: faultValue, description: faultDescription || '' }),
      ...faultCatalog.options,
    ];
  }, [faultCatalog.options, faultDescription, faultValue]);
  const selectedCatalogFault = useMemo(
    () => faultCatalog.options.find((option) => option.value === faultValue),
    [faultCatalog.options, faultValue]
  );

  /**
   * Confirms the row delete.
   */
  const confirmRowDelete = useCallback(() => {
    confirm({
      title: 'Zmazanie nedostatku',
      content: 'Naozaj chcete zmazať tento nedostatok?',
      onConfirm: () => onDelete(index),
    });
  }, [confirm, index, onDelete]);

  const visibleCols =
    (faultInput.spec.hidden ? 0 : 1) +
    (severityInput.spec.hidden ? 0 : 1) +
    (moreInfoInput.spec.hidden ? 0 : 1) +
    (observationInput.spec.hidden ? 0 : 1) +
    (attachmentsInput.spec.hidden ? 0 : 1);

  useEffect(() => {
    const listFaultSeverities = getAction('AuthListFaultSeverities');
    listFaultSeverities().then(setFaultSeverities).catch(panic);
  }, [getAction]);

  useEffect(() => {
    const severity = faultSeverities.find((s) => String(s.faultSeverityId) === severityId);
    if (severity && !severityInput.spec.hidden) {
      form.setFieldValue(`faults.${index}.classificationCode`, severity.classificationCode ?? '');
    }
  }, [severityId, faultSeverities]);

  useEffect(() => {
    if (selectedCatalogFault && selectedCatalogFault.description !== (faultDescription ?? '')) {
      form.setFieldValue(`faults.${index}.description`, selectedCatalogFault.description);
    }
  }, [faultDescription, form, index, selectedCatalogFault]);

  return (
    <Flex align="flex-start" style={{ borderBottom: `1px solid ${theme.colors.gray[1]}` }}>
      <SimpleGrid spacing={24} py={24} px={16} cols={visibleCols} style={{ flex: '1 0 0' }}>
        {!faultInput.spec.hidden && (
          <RevisionPredefinedDataPicker
            searchable
            size="md"
            placeholder={faultInput.spec.label}
            loading={faultCatalog.loading}
            options={faultOptions}
            value={
              faultValue
                ? {
                    name: faultValue,
                    description: faultDescription || '',
                  }
                : undefined
            }
            deviceTypeName={deviceTypeName}
            predefinedDataTypeId={faultCatalog.predefinedDataTypeId ?? 0}
            predefinedDataTypeName={faultCatalog.predefinedDataTypeName}
            onCatalogOptionCreate={faultCatalog.upsertCatalogOption}
            onChange={(nextValue) => {
              form.setFieldValue(`faults.${index}.fault`, nextValue?.name ?? '');
              form.setFieldValue(`faults.${index}.description`, nextValue?.description ?? '');
            }}
          />
        )}
        {!severityInput.spec.hidden && (
          <FaultSeveritySelect
            searchable
            size="md"
            placeholder={severityInput.spec.label}
            loadingPlaceholder="Závažnosť"
            excludedFaultSeverityIds={[...DISCONTINUED_FAULT_SEVERITY_IDS]}
            keepExcludedValueVisible
            {...form.getInputProps(`faults.${index}.severity`)}
          />
        )}
        {!moreInfoInput.spec.hidden && (
          <Input size="md" placeholder={moreInfoInput.spec.label} {...form.getInputProps(`faults.${index}.moreInfo`)} />
        )}
        {!observationInput.spec.hidden && (
          <Checkbox
            size="md"
            label={observationInput.spec.label}
            {...form.getInputProps(`faults.${index}.observation`, { type: 'checkbox' })}
          />
        )}
        {!attachmentsInput.spec.hidden && (
          <DeviceFaultAttachments index={index} form={form} spec={attachmentsInput.spec as IFormInputSpecFileList} />
        )}
      </SimpleGrid>

      <Flex w={72} h="auto" py={24} px={16} align="flex-start" gap={12} style={{ flexShrink: 0 }}>
        <ActionIcon variant="danger-secondary" size="md" onClick={confirmRowDelete}>
          <IconTrashX />
        </ActionIcon>
      </Flex>
    </Flex>
  );
}
